import './App.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { setDefaultOptions } from 'date-fns';
import { it } from 'date-fns/locale';
import i18next from 'i18next';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { lazy, Suspense } from 'react';
import { I18nProvider } from 'react-aria-components';
import { HelmetProvider } from 'react-helmet-async';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';
import { z } from 'zod';
import { zodI18nMap } from 'zod-i18n-map';
import translation from 'zod-i18n-map/locales/it/zod.json';

import { queryClient } from '@/api';
import { Breakpoints } from '@/components/primitives/breakpoints';
import { Toaster } from '@/components/primitives/sonner';

import { useHomeRouteAccount } from './api/user-parameters';
import { ErrorBoundary } from './components/error-boundary';
import { Env, useBreakpoint } from './lib';
import { hydrateTutorial } from './state/home-tutorial';

const PublicLayout = lazy(() => import('./components/layout/public-layout'));
const LoggedLayout = lazy(() => import('./components/layout/logged-layout'));
const LoginPage = lazy(() => import('./features/login'));
const ForgotPage = lazy(() => import('./features/forgot/forgot.page'));
const ResetPage = lazy(() => import('./features/reset/reset.page'));
const ChangePasswordPage = lazy(
  () => import('./features/change-password/change-password.page'),
);

const TimesheetPage = lazy(() => import('./features/timesheet'));
const CreateEventModal = lazy(
  () => import('@/features/timesheet/events/create-event.page'),
);
const EditEventModal = lazy(
  () => import('./features/timesheet/events/edit-event.page'),
);

const ClientsPage = lazy(() => import('./features/clients'));
const ClientCreateModal = lazy(
  () => import('./features/clients/create-client.page'),
);
const ClientEditModal = lazy(
  () => import('./features/clients/edit-client.page'),
);

const EmployeesPage = lazy(() => import('./features/employees'));
const EmployeeCreateModal = lazy(
  () => import('@/features/employees/create-employee.page'),
);
const EmployeeEditModal = lazy(
  () => import('./features/employees/edit-employee.page'),
);
const ProfilePage = lazy(() => import('./features/profile'));

const CostCentersPage = lazy(() => import('./features/cost-centers'));
const CostCenterCreateModal = lazy(
  () => import('./features/cost-centers/create-cost-center.page'),
);
const CostCenterEditModal = lazy(
  () => import('./features/cost-centers/edit-cost-center.page'),
);

const OfficesPage = lazy(() => import('./features/offices'));
const OfficeCreateModal = lazy(
  () => import('./features/offices/create-office.page'),
);
const OfficeEditModal = lazy(
  () => import('./features/offices/edit-office.page'),
);

const MacroProjectsPage = lazy(() => import('./features/macro-projects'));
const MacroProjectCreateModal = lazy(
  () => import('./features/macro-projects/create-macro-project.page'),
);
const MacroProjectEditModal = lazy(
  () => import('./features/macro-projects/edit-macro-project.page'),
);

const ProjectPrioritiesPage = lazy(
  () => import('./features/project-priorities'),
);
const ProjectPriorityCreateModal = lazy(
  () => import('./features/project-priorities/create-project-priority.page'),
);
const ProjectPriorityEditModal = lazy(
  () => import('./features/project-priorities/edit-project-priority.page'),
);

const ProjectStatusesPage = lazy(() => import('./features/project-statuses'));
const ProjectStatusCreateModal = lazy(
  () => import('./features/project-statuses/create-project-status.page'),
);
const ProjectStatusEditModal = lazy(
  () => import('./features/project-statuses/edit-project-status.page'),
);

const ProjectsPage = lazy(() => import('./features/projects'));
const ProjectsCreateModal = lazy(
  () => import('./features/projects/create-project.page'),
);
const ProjectsEditModal = lazy(
  () => import('./features/projects/edit-project.page'),
);

const ActivitiesPage = lazy(() => import('./features/activities'));
const CreateActivityModal = lazy(
  () => import('@/features/activities/activity/create-activity.page'),
);
const EditActivityModal = lazy(
  () => import('./features/activities/activity/edit-activity.page'),
);
const MenuManagerPage = lazy(() => import('./features/menu-manager'));
const SocietaPage = lazy(() => import('./features/societa'));

// lng and resources key depend on your locale.
i18next.init({
  lng: 'it',
  resources: {
    it: { zod: translation },
  },
});
z.setErrorMap(zodI18nMap);

setDefaultOptions({ locale: it });

function HomeRoute() {
  const { data: account, error } = useHomeRouteAccount();

  if (error?.response?.status === 401) {
    return <Navigate to="/login" replace />;
  }

  if (error) {
    throw error;
  }

  if (account?.role === 'cliente') {
    return <Navigate to="/activities" replace />;
  }

  return (
    <LoggedLayout fullWidth>
      <TimesheetPage />
    </LoggedLayout>
  );
}

const router = createBrowserRouter(
  [
    {
      path: '/',
      errorElement: <ErrorBoundary />,
      element: (
        <Suspense fallback={null}>
          <HomeRoute />
        </Suspense>
      ),
      children: [
        {
          path: 'create',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <CreateEventModal />
            </Suspense>
          ),
        },
        {
          path: 'edit/:id',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <EditEventModal />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: '/clients',
      errorElement: <ErrorBoundary />,
      handle: {
        title: 'Lista Clienti',
      },
      element: (
        <Suspense fallback={null}>
          <LoggedLayout>
            <ClientsPage />
          </LoggedLayout>
        </Suspense>
      ),
      children: [
        {
          path: 'create',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <ClientCreateModal />
            </Suspense>
          ),
        },
        {
          path: 'edit/:id',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <ClientEditModal />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: '/users',
      errorElement: <ErrorBoundary />,
      handle: {
        title: 'Lista Utenti',
      },
      element: (
        <Suspense fallback={null}>
          <LoggedLayout>
            <EmployeesPage />
          </LoggedLayout>
        </Suspense>
      ),
      children: [
        {
          path: 'create',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <EmployeeCreateModal />
            </Suspense>
          ),
        },
        {
          path: 'edit/:id',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <EmployeeEditModal />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: '/cost-centers',
      errorElement: <ErrorBoundary />,
      handle: {
        title: 'Lista Centri di Costo',
      },
      element: (
        <Suspense fallback={null}>
          <LoggedLayout>
            <CostCentersPage />
          </LoggedLayout>
        </Suspense>
      ),
      children: [
        {
          path: 'create',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <CostCenterCreateModal />
            </Suspense>
          ),
        },
        {
          path: 'edit/:id',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <CostCenterEditModal />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: '/offices',
      errorElement: <ErrorBoundary />,
      handle: {
        title: 'Lista Uffici',
      },
      element: (
        <Suspense fallback={null}>
          <LoggedLayout>
            <OfficesPage />
          </LoggedLayout>
        </Suspense>
      ),
      children: [
        {
          path: 'create',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <OfficeCreateModal />
            </Suspense>
          ),
        },
        {
          path: 'edit/:id',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <OfficeEditModal />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: '/macro-projects',
      errorElement: <ErrorBoundary />,
      handle: {
        title: 'Lista Macro Progetti',
      },
      element: (
        <Suspense fallback={null}>
          <LoggedLayout>
            <MacroProjectsPage />
          </LoggedLayout>
        </Suspense>
      ),
      children: [
        {
          path: 'create',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <MacroProjectCreateModal />
            </Suspense>
          ),
        },
        {
          path: 'edit/:id',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <MacroProjectEditModal />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: '/project-priorities',
      errorElement: <ErrorBoundary />,
      handle: {
        title: 'Lista Priorità Progetti',
      },
      element: (
        <Suspense fallback={null}>
          <LoggedLayout>
            <ProjectPrioritiesPage />
          </LoggedLayout>
        </Suspense>
      ),
      children: [
        {
          path: 'create',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <ProjectPriorityCreateModal />
            </Suspense>
          ),
        },
        {
          path: 'edit/:id',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <ProjectPriorityEditModal />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: '/project-statuses',
      errorElement: <ErrorBoundary />,
      handle: {
        title: 'Lista Stato Progetti',
      },
      element: (
        <Suspense fallback={null}>
          <LoggedLayout>
            <ProjectStatusesPage />
          </LoggedLayout>
        </Suspense>
      ),
      children: [
        {
          path: 'create',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <ProjectStatusCreateModal />
            </Suspense>
          ),
        },
        {
          path: 'edit/:id',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <ProjectStatusEditModal />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: '/projects',
      errorElement: <ErrorBoundary />,
      handle: {
        title: 'Lista Progetti',
      },
      element: (
        <Suspense fallback={null}>
          <LoggedLayout>
            <ProjectsPage />
          </LoggedLayout>
        </Suspense>
      ),
      children: [
        {
          path: 'create',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <ProjectsCreateModal />
            </Suspense>
          ),
        },
        {
          path: 'edit/:id',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <ProjectsEditModal />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: '/activities',
      errorElement: <ErrorBoundary />,
      handle: {
        title: 'Lista Attività',
      },
      element: (
        <Suspense fallback={null}>
          <LoggedLayout fullWidth>
            <ActivitiesPage />
          </LoggedLayout>
        </Suspense>
      ),
      children: [
        {
          path: 'create',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <CreateActivityModal />
            </Suspense>
          ),
        },
        {
          path: 'edit/:id',
          errorElement: <ErrorBoundary />,
          element: (
            <Suspense fallback={null}>
              <EditActivityModal />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: '/profile',
      errorElement: <ErrorBoundary />,
      handle: {
        title: 'Profilo',
      },
      element: (
        <Suspense fallback={null}>
          <LoggedLayout>
            <ProfilePage />
          </LoggedLayout>
        </Suspense>
      ),
    },
    {
      path: '/change-password',
      errorElement: <ErrorBoundary />,
      handle: {
        title: 'Cambia Password',
      },
      element: (
        <Suspense fallback={null}>
          <LoggedLayout>
            <ChangePasswordPage />
          </LoggedLayout>
        </Suspense>
      ),
    },
    {
      path: '/menu-manager',
      errorElement: <ErrorBoundary />,
      handle: {
        title: 'Gestione Menu',
      },
      element: (
        <Suspense fallback={null}>
          <LoggedLayout>
            <MenuManagerPage />
          </LoggedLayout>
        </Suspense>
      ),
    },
    {
      path: '/societa',
      errorElement: <ErrorBoundary />,
      handle: {
        title: 'Società',
      },
      element: (
        <Suspense fallback={null}>
          <LoggedLayout>
            <SocietaPage />
          </LoggedLayout>
        </Suspense>
      ),
    },
    {
      path: '/login',
      errorElement: <ErrorBoundary />,
      element: (
        <Suspense fallback={null}>
          <PublicLayout>
            <LoginPage />
          </PublicLayout>
        </Suspense>
      ),
    },
    {
      path: '/forgot',
      errorElement: <ErrorBoundary />,
      element: (
        <Suspense fallback={null}>
          <PublicLayout>
            <ForgotPage />
          </PublicLayout>
        </Suspense>
      ),
    },
    {
      path: '/reset',
      errorElement: <ErrorBoundary />,
      element: (
        <Suspense fallback={null}>
          <PublicLayout>
            <ResetPage />
          </PublicLayout>
        </Suspense>
      ),
    },
    {
      path: '*',
      errorElement: <ErrorBoundary />,
      element: <Navigate to="/" replace />,
    },
  ],
  {
    basename: Env.APP_BASE_URL,
  },
);
// TODO WORKAROUND
// @ts-expect-error react refresh si lamenterebbe se router venisse esportato
window.router = router;

hydrateTutorial();

function App() {
  return (
    <div className="h-svh font-sans">
      <main className="flex size-full">
        <QueryClientProvider client={queryClient}>
          <NuqsAdapter>
            <HelmetProvider>
              <I18nProvider locale="it">
                <RouterProvider router={router} />
              </I18nProvider>
            </HelmetProvider>
          </NuqsAdapter>
          {/* {import.meta.env.MODE === 'development' && (
            <div className="pointer-events-auto">
              <ReactQueryDevtools initialIsOpen={false} />
            </div>
          )} */}
        </QueryClientProvider>

        {import.meta.env.MODE === 'development' && <Breakpoints />}
      </main>
      <ResponsiveToaster />
    </div>
  );
}

const ResponsiveToaster = () => {
  const { isBelowSm: isMobile } = useBreakpoint('sm');

  if (isMobile) {
    return <Toaster position="top-center" />;
  }
  return <Toaster position="bottom-left" />;
};

export default App;
